import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  TextField,
  Checkbox,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Autocomplete,
} from '@mui/material';
import { AppState } from '../../../store';
import {
  getAllFileTypes,
  getOneFileType,
  upsertFileType,
} from '../../../store/admin/file-type-configuration/actions';
import useStyles from './styles';
import { FileTypesRecord } from '../../../store/admin/file-type-configuration/types';

const ManageFileTypes = () => {
  const classes = useStyles();
  const configIds = useSelector((state: AppState) => state.admin.autocompleteList);
  const mastershipValues = useSelector((state: AppState) => state.admin.mastershipList);
  const transferDirectionValues = useSelector(
    (state: AppState) => state.admin.transferDirectionList,
  );
  const commandQueueLimitValues = useSelector(
    (state: AppState) => state.admin.commandQueueLimitList,
  );
  const classificationValues = useSelector((state: AppState) => state.admin.classificationList);
  const fileTypeData = useSelector((state: AppState) => state.admin.manageFileTypes);
  const admin = useSelector((state: AppState) => state.admin);
  const dispatch = useDispatch();
  const defaultProps = {
    options: configIds,
    className: classes.textBox,
  };
  const [transferDirection, setTransferDirection] = useState<string>('');
  const [extension, setExtension] = useState<string>('');
  const [classification, setClassification] = useState<string>('');
  const [fileTypeId, setFileTypeId] = useState<string>('');
  const [fileTypeName, setFileTypeName] = useState<string>('');
  const [mastership, setMastership] = useState<string>('');
  const [isTransferOverrideEnabled, setIsTransferOverrideEnabled] = useState<boolean>(false);
  const [isAutoSyncEnabled, setIsAutoSyncEnabled] = useState<boolean>(false);
  const [isPushEnabled, setIsPushEnabled] = useState<boolean>(false);
  const [isShaSyncSupported, setIsShaSyncSupported] = useState<boolean>(false);
  const [isHashingEnabled, setIsHashingEnabled] = useState<boolean>(false);
  const [commandQueueLimit, setCommandQueueLimit] = useState<string>('');
  const [isSwapsEnabled, setIsSwapsEnabled] = useState<boolean>(false);
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);

  useEffect(() => {
    dispatch(getAllFileTypes());
  }, []);
  useEffect(() => {
    if (fileTypeId.length === 5) {
      setTransferDirection(fileTypeData.transferDirection);
      setExtension(fileTypeData.extension);
      setClassification(fileTypeData.classification);
      setMastership(fileTypeData.mastership);
      setIsTransferOverrideEnabled(fileTypeData.isTransferOverrideEnabled);
      setIsAutoSyncEnabled(fileTypeData.isAutoSyncEnabled);
      setIsPushEnabled(fileTypeData.isPushEnabled);
      setIsShaSyncSupported(fileTypeData.isShaSyncSupported);
      setIsHashingEnabled(fileTypeData.isHashingEnabled);
      setCommandQueueLimit(fileTypeData.commandQueueLimit);
      setIsSwapsEnabled(fileTypeData.isSwapsEnabled);
      setFileTypeName(fileTypeData.fileTypeName);
      setIsDataLoaded(true);
    }
  }, [admin]);

  const isSaveEnabled = () => {
    if (
      transferDirection === fileTypeData.transferDirection &&
      extension === fileTypeData.extension &&
      classification === fileTypeData.classification &&
      mastership === fileTypeData.mastership &&
      isTransferOverrideEnabled === fileTypeData.isTransferOverrideEnabled &&
      isAutoSyncEnabled === fileTypeData.isAutoSyncEnabled &&
      isPushEnabled === fileTypeData.isPushEnabled &&
      isShaSyncSupported === fileTypeData.isShaSyncSupported &&
      isHashingEnabled === fileTypeData.isHashingEnabled &&
      commandQueueLimit === fileTypeData.commandQueueLimit &&
      isSwapsEnabled === fileTypeData.isSwapsEnabled &&
      fileTypeName === fileTypeData.fileTypeName
    ) {
      return false;
    }
    return true;
  };

  const regExpOnlyNumbers = /\D/;

  const boxProperties = {
    p: 3,
    className: classes.box,
  };

  const queryFile = (fileId: string) => {
    dispatch(getOneFileType(fileId));
  };

  const saveFileType = async () => {
    dispatch(
      upsertFileType(
        {
          transferDirection,
          fileTypeId,
          extension,
          mastership,
          isTransferOverrideEnabled,
          isAutoSyncEnabled,
          isPushEnabled,
          isShaSyncSupported,
          isHashingEnabled,
          commandQueueLimit,
          isSwapsEnabled,
          fileTypeName,
          classification,
          isOnDemandSupported: classification === "PLMessage",
        } as FileTypesRecord,
        configIds,
      ),
    );
  };
  return (
    <>
      <Grid container direction="row" alignItems="stretch">
        <Grid item xs={12} lg={6}>
          <Box {...boxProperties}>
            <InputLabel>File Type #</InputLabel>
            <Autocomplete
              style={{ paddingTop: '6px' }}
              {...defaultProps}
              id="file-type-id-search"
              freeSolo
              className={classes.autocomplete}
              onInputChange={(event: any, newInputValue: string) => {
                setIsDataLoaded(false);
                if (newInputValue.length !== 5 || regExpOnlyNumbers.test(newInputValue)) {
                  return;
                }
                setFileTypeId(newInputValue);
                if (newInputValue.length === 5) {
                  queryFile(newInputValue);
                }
              }}
              renderInput={(parameters) => {
                Object.assign(parameters.inputProps, { maxLength: 5 });
                return (
                  <TextField
                    variant="standard"
                    className={classes.textBox}
                    {...parameters}
                    margin="normal"
                  />
                );
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Box {...boxProperties}>
            <InputLabel>File Extension</InputLabel>
            <TextField
              variant="standard"
              value={extension}
              onChange={(event) => setExtension(event.target.value as string)}
              className={classes.textBox}
              disabled={!isDataLoaded}
            />
          </Box>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Box {...boxProperties}>
            <InputLabel>Description</InputLabel>
            <TextField
              variant="standard"
              value={fileTypeName}
              onChange={(event) => setFileTypeName(event.target.value as string)}
              className={classes.textBox}
              disabled={configIds.includes(fileTypeId)}
            />
          </Box>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Box {...boxProperties}>
            <InputLabel>Classification</InputLabel>

            <Select
              variant="standard"
              value={classification}
              className={classes.select}
              onChange={(event) => setClassification(event.target.value as string)}
              disabled={!isDataLoaded}
            >
              {(classificationValues as Array<string>).map((classificationValue: string) => (
                <MenuItem value={classificationValue}> {classificationValue}</MenuItem>
              ))}
            </Select>
          </Box>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Box {...boxProperties}>
            <InputLabel>Transfer Direction</InputLabel>
            <Select
              variant="standard"
              value={transferDirection}
              className={classes.select}
              onChange={(event) => setTransferDirection(event.target.value as string)}
              disabled={!isDataLoaded}
            >
              {(transferDirectionValues as Array<string>).map((transferDirectionValue: string) => (
                <MenuItem value={transferDirectionValue}> {transferDirectionValue}</MenuItem>
              ))}
            </Select>
          </Box>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Box {...boxProperties}>
            <InputLabel>Mastership</InputLabel>
            <Select
              variant="standard"
              value={mastership}
              className={classes.select}
              onChange={(event) => setMastership(event.target.value as string)}
              disabled={!isDataLoaded}
            >
              {(mastershipValues as Array<string>).map((mastershipValue: string) => (
                <MenuItem value={mastershipValue}> {mastershipValue}</MenuItem>
              ))}
            </Select>
          </Box>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Box {...boxProperties}>
            <InputLabel>Sent Upon Request</InputLabel>
            <Checkbox
              className={classes.checkbox}
              checked={isAutoSyncEnabled}
              onChange={(event) => {
                setIsAutoSyncEnabled(event.target.checked as boolean);
              }}
              disabled={!isDataLoaded}
            />
          </Box>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Box {...boxProperties}>
            <InputLabel>API Push Enabled</InputLabel>
            <Checkbox
              className={classes.checkbox}
              checked={isPushEnabled}
              onChange={(event) => {
                setIsPushEnabled(event.target.checked as boolean);
              }}
              disabled={!isDataLoaded}
            />
          </Box>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Box {...boxProperties}>
            <InputLabel>SHA Sync Enabled</InputLabel>
            <Checkbox
              className={classes.checkbox}
              checked={isShaSyncSupported}
              onChange={(event) => {
                setIsShaSyncSupported(event.target.checked as boolean);
              }}
              disabled={!isDataLoaded}
            />
          </Box>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Box {...boxProperties}>
            <InputLabel>Hash Check Enabled</InputLabel>
            <Checkbox
              className={classes.checkbox}
              checked={isHashingEnabled}
              disabled={!isDataLoaded}
              onChange={(event) => {
                setIsHashingEnabled(event.target.checked as boolean);
              }}
            />
          </Box>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Box {...boxProperties}>
            <InputLabel>Swaps Enabled</InputLabel>
            <Checkbox
              className={classes.checkbox}
              checked={isSwapsEnabled}
              disabled={!isDataLoaded}
              onChange={(event) => {
                setIsSwapsEnabled(event.target.checked as boolean);
              }}
            />
          </Box>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Box {...boxProperties}>
            <InputLabel>Transfer Override Enabled</InputLabel>
            <Checkbox
              className={classes.checkbox}
              checked={isTransferOverrideEnabled}
              disabled={!isDataLoaded}
              onChange={(event) => {
                setIsTransferOverrideEnabled(event.target.checked as boolean);
              }}
            />
          </Box>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Box {...boxProperties}>
            <InputLabel>Command Queue Limit</InputLabel>
            <Select
              variant="standard"
              value={commandQueueLimit}
              className={classes.select}
              disabled={!isDataLoaded}
              onChange={(event) => {
                setCommandQueueLimit(event.target.value as string);
              }}
            >
              {(commandQueueLimitValues as Array<string>).map((commandQueueLimitValue: string) => (
                <MenuItem value={commandQueueLimitValue}> {commandQueueLimitValue}</MenuItem>
              ))}
            </Select>
          </Box>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Box {...boxProperties}>
            <Button
              className={classes.button}
              variant="outlined"
              disabled={!isSaveEnabled()}
              onClick={() => {
                saveFileType();
              }}
            >
              Save
            </Button>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default ManageFileTypes;
